import { Button } from "@/components/ui/button/button";
import { buttonVariants } from "@/components/ui/button/button-variants";
import PageTransition from "@/components/ui/PageTransition";
import SubmitButton from "@/components/ui/submit-button";
import { useToast } from "@/components/ui/use-toast";
import { useSignInUser } from "@/features/auth/useSignInUser";
import { useSignInUserWithOtp } from "@/features/auth/useSignInUserWithOtp";
import { getHelpCenterUrl } from "@/lib/utils";
import { authService } from "@/services/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import FloatingInput from "../../components/ui/floatingInput";
import { FormField } from "../../components/ui/form/form";
import { ArrowRightIcon } from "../../components/ui/icons";
import MaintenanceNotice from "./MaintenanceNotice";
import SignInTwoFactorPage from "./SignInTwoFactorPage";

const formSchema = z.object({
	email: z
		.string()
		.min(2, {
			message: "Email must be at least 2 characters.",
		})
		.trim()
		.email({
			message: "Please enter a valid email address.",
		}),
	password: z.string().min(8, {
		message: "Password must be at least 8 characters.",
	}),
});

type FormSchema = z.infer<typeof formSchema>;

const SignInMailPage = () => {
	const navigate = useNavigate();
	const form = useForm<FormSchema>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});
	const signinMutation = useSignInUser();
	const signInWithOtpMutation = useSignInUserWithOtp();
	const resendCodeMutation = useSignInUser();
	const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
	const [resendCountdown, setResendCountdown] = useState(30);

	const { toast } = useToast();

	const showSignInErrorToast = (
		error: { message: string; http_status?: number },
		fallbackTitle: string,
	) => {
		if (error.http_status === 429) {
			toast({
				title: "Too many attempts",
				description:
					"You've made too many attempts. Please wait a little and try again.",
				variant: "destructive",
			});
			return;
		}

		toast({
			title: fallbackTitle,
			description: error.message,
			variant: "destructive",
		});
	};

	useEffect(() => {
		if (!requiresTwoFactor || resendCountdown === 0) return;

		const timeout = window.setTimeout(() => {
			setResendCountdown((seconds) => seconds - 1);
		}, 1000);

		return () => window.clearTimeout(timeout);
	}, [requiresTwoFactor, resendCountdown]);

	const onSubmit = (values: FormSchema) => {
		signinMutation.mutate(
			{
				email: values.email,
				password: values.password,
			},
			{
				onSuccess: (data) => {
					if ("requires_2fa" in data) {
						setRequiresTwoFactor(true);
						return;
					}

					authService.setToken(data);
					navigate({
						to: "/main/home",
					});
				},
				onError: (error) => {
					showSignInErrorToast(error, "Login Failed");
				},
			},
		);
	};

	const handleTwoFactorSubmit = (otpCode: string) => {
		signInWithOtpMutation.mutate(
			{
				email: form.getValues("email"),
				password: form.getValues("password"),
				otp_code: otpCode,
			},
			{
				onSuccess: (data) => {
					authService.setToken(data);
					navigate({
						to: "/main/home",
					});
				},
				onError: (error) => {
					showSignInErrorToast(error, "Login Failed");
				},
			},
		);
	};

	const handleResendCode = () => {
		if (resendCountdown > 0 || resendCodeMutation.isPending) return;

		resendCodeMutation.mutate(
			{
				email: form.getValues("email"),
				password: form.getValues("password"),
			},
			{
				onSuccess: () => {
					setResendCountdown(30);
					toast({
						title: "2FA Code Sent",
						description: "A new 2FA code has been sent to your email address.",
					});
				},
				onError: (error) => {
					showSignInErrorToast(error, "Error Sending 2FA Code");
				},
			},
		);
	};

	if (requiresTwoFactor) {
		return (
			<PageTransition>
				<SignInTwoFactorPage
					resendCountdown={resendCountdown}
					isResending={resendCodeMutation.isPending}
					isSubmitting={signInWithOtpMutation.isPending}
					onResendCode={handleResendCode}
					onSubmit={handleTwoFactorSubmit}
				/>
			</PageTransition>
		);
	}

	return (
		<div className="flex flex-col justify-between px-6 py-10 bg-white max-w-[800px] h-[100dvh]  w-full mx-auto">
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col items-center w-full gap-8 max-w-md mx-auto">
					<MaintenanceNotice />
					{/* Title */}
					<div className="text-center">
						<h1 className="mb-2 text-3xl font-bold text-textDark">
							Welcome back
						</h1>
						<p className="text-sm text-textDark">Sign in to your account</p>
					</div>

					{/* Inputs */}

					<div className="flex flex-col w-full gap-4">
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FloatingInput
									label="Email address"
									type="email"
									value={field.value}
									onChange={field.onChange}
									error={form.formState.errors.email}
								/>
							)}
						/>
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FloatingInput
									label="Password"
									type="password"
									value={field.value}
									onChange={field.onChange}
									error={form.formState.errors.password}
								/>
							)}
						/>
					</div>

					{/* Forgot + Submit */}
					<div className="flex items-center justify-between w-full">
						<Button
							type="button"
							variant="link"
							className="text-sm text-textDark font-normal px-0"
							onClick={() =>
								navigate({
									to: "/password-recovery",
								})
							}
						>
							Forgot password?
						</Button>
						<SubmitButton
							mutationStatus={signinMutation.status}
							loadingText=""
							variant={"default"}
							size="squareLarge"
							className="text-white rounded-xl bg-primary-600 hover:bg-primary-500 w-[58px] h-[58px]"
						>
							<ArrowRightIcon />
						</SubmitButton>
					</div>
				</div>
			</form>
			<div className="flex-1"></div>
			{/* Social Sign-in */}

			{/* Support */}
			<div className="mt-6 text-sm text-center">
				<div className="text-textDark text-sm mb-4">
					Not a member?{" "}
					<Link
						className={buttonVariants({
							variant: "ghost",
							size: "sm",
						})}
						to={"/sign-up"}
						search={{ promo: undefined }}
					>
						Sign up
					</Link>
				</div>
				<a
					href={getHelpCenterUrl()}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-center cursor-pointer"
				>
					<span className="text-textDark">Need Help?</span>{" "}
					<span className="text-textDark font-semibold">Contact Support</span>
				</a>
			</div>
		</div>
	);
};

export default SignInMailPage;
